import '/src/scss/style.scss';

// No `import.meta.hot.accept()` here on purpose. Nothing below is HMR-aware —
// the @spon modules bind listeners on mount and are never torn down — so
// self-accepting made every JS edit re-run this entry in place, stacking a
// second copy of loadApp(), each loadModule() and the htmx.on() handlers.
// Without it the update propagates to the root unaccepted and Vite does a full
// page reload. CSS HMR is unaffected: style.scss self-accepts on its own, and
// it is also what pulls /@vite/client into the page.

import { loadApp, loadModule } from '@spon/core';
import 'lazysizes';
import Header from '@/globals/Header';

import SetVh from '@/globals/SetVh';
import FadeIn from '@/globals/FadeIn';
import AnchorLink from '@/globals/AnchorLink';

import '@/plugins/logger';
import webfontloader from '@/plugins/webfontloader';
import detectTab from '@/utils/detectTab';

import activeLinks from '@/utils/activeLinks';
import localLinks from '@/utils/localLinks';

webfontloader();

// load from data-behaviours
loadApp(name => import(`./behaviours/${name}.js`), document.body); // eslint-disable-line

loadModule({
	module: Header,
	id: 'header',
	node: document.getElementById('header'),
	keepAlive: false,
});

loadModule({
	module: SetVh,
	id: 'set-vh',
	node: document.body,
	keepAlive: false,
});

loadModule({
	module: FadeIn,
	id: 'fadein',
	node: document.body,
	keepAlive: false,
});

loadModule({
	module: AnchorLink,
	id: 'anchor-link',
	node: document.body,
	keepAlive: false,
});

detectTab();
activeLinks(window.location.pathname);
localLinks();

// Fade during loading. Dont need to remove as dom is updated
// https://htmx.org/events
if (typeof htmx !== 'undefined') {
	// eslint-disable-next-line
	htmx.on('htmx:beforeSend', ({ target }) => {
		const results = document.getElementById('results');
		if (results) {
			results.classList.add('is-loading');
		}
	});

	// Scroll to top of `sprig` container when interacting; but only when `scroll` is in Param (eg not filtering; but probably pagination)
	// eslint-disable-next-line
	htmx.on(
		'htmx:beforeSwap',
		({
			detail: {
				requestConfig: { triggeringEvent },
			},
			target,
		}) => {
			localLinks(target);

			if (triggeringEvent) {
				const { srcElement } = triggeringEvent;
				if (!srcElement) return;

				const shouldScroll = srcElement.closest('.c-pagination');
				if (srcElement && shouldScroll) {
					window.scrollTo({
						top: target.getBoundingClientRect().top + window.scrollY - 100,
						behavior: 'smooth',
					});
				}
			}
		}
	);

	// eslint-disable-next-line
	htmx.on('htmx:afterSwap', ({ target }) => {
		const results = document.getElementById('results');
		if (results) {
			results.classList.remove('is-loading');
		}

		// Unlike localLinks, activeLinks takes the *path* to match against, not a
		// container - it always sweeps the whole document, so links outside the
		// swapped region (filter nav, pagination) lose a now-stale `is-current`
		// too. Sprig's pushUrl has already landed by here: htmx updates history in
		// its beforeSwapCallback, ahead of both the swap and this event.
		activeLinks(window.location.pathname);
		localLinks(target);
		// We need to fire behaviours inside dynamically loaded blocks
		loadApp(name => import(`./behaviours/${name}.js`), target);
	});
}

document.addEventListener('afterBlitzInjectAll', event => {
	loadModule({
		module: Header,
		id: 'header',
		node: document.getElementById('header'),
		keepAlive: false,
	});
});
